import { useEffect, useState, type FormEvent } from "react";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../components/ui/Toast";
import { api, ApiClientError } from "../lib/api";
import PageHeader from "../components/layout/PageHeader";
import { Spinner } from "../components/ui/Spinner";
import {
  MIN_PASSWORD_LENGTH,
  PAGE_KEYS,
  PAGE_LABELS,
  type AdminUser,
  type Client,
  type ClientPageAccessResponse,
  type PageKey,
  type Role,
} from "@shared/types";

export default function Settings() {
  const { user, isBtzl } = useAuth();

  return (
    <div className="max-w-3xl">
      <PageHeader
        title="Settings"
        subtitle={isBtzl ? "Manage your account and portal users" : "Manage your account"}
      />
      <div className="space-y-8">
        <ChangePassword />
        {isBtzl && <PageAccess />}
        {isBtzl && <UserManagement currentUserId={user?.id ?? -1} />}
      </div>
    </div>
  );
}

/* ------------------------------ Own password ------------------------------ */

function ChangePassword() {
  const toast = useToast();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("New passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/api/auth/password", { currentPassword: current, newPassword: next });
      toast.success("Password updated");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not update password");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="card p-6">
      <h2 className="text-base font-semibold text-ink-900">Change your password</h2>
      <p className="mt-0.5 text-sm text-ink-500">
        You will stay signed in here; any other active sessions will be signed out.
      </p>
      <form onSubmit={onSubmit} className="mt-4 grid max-w-md gap-4">
        <Field label="Current password">
          <input
            type="password"
            autoComplete="current-password"
            required
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            className="input"
          />
        </Field>
        <Field label={`New password (min ${MIN_PASSWORD_LENGTH} characters)`}>
          <input
            type="password"
            autoComplete="new-password"
            required
            minLength={MIN_PASSWORD_LENGTH}
            value={next}
            onChange={(e) => setNext(e.target.value)}
            className="input"
          />
        </Field>
        <Field label="Confirm new password">
          <input
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="input"
          />
        </Field>
        <div>
          <button type="submit" disabled={submitting} className="btn-primary">
            {submitting ? "Saving…" : "Update password"}
          </button>
        </div>
      </form>
    </section>
  );
}

/* --------------------------- Admin: manage users -------------------------- */

function UserManagement({ currentUserId }: { currentUserId: number }) {
  const toast = useToast();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    const d = await api.get<{ users: AdminUser[] }>("/api/users");
    setUsers(d.users);
  }

  useEffect(() => {
    Promise.all([
      api.get<{ users: AdminUser[] }>("/api/users"),
      api.get<{ clients: Client[] }>("/api/clients"),
    ])
      .then(([u, c]) => {
        setUsers(u.users);
        setClients(c.clients);
      })
      .catch((err) =>
        toast.error(err instanceof ApiClientError ? err.message : "Could not load users")
      )
      .finally(() => setLoading(false));
  }, [toast]);

  return (
    <section className="card p-6">
      <h2 className="text-base font-semibold text-ink-900">Users &amp; access</h2>
      <p className="mt-0.5 text-sm text-ink-500">
        Create portal users and reset any user's password.
      </p>

      <CreateUser clients={clients} onCreated={refresh} />

      <div className="mt-6">
        {loading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : (
          <UserList users={users} currentUserId={currentUserId} onChanged={refresh} />
        )}
      </div>
    </section>
  );
}

function CreateUser({ clients, onCreated }: { clients: Client[]; onCreated: () => Promise<void> }) {
  const toast = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<Role>("client");
  const [clientId, setClientId] = useState<number | "">(clients[0]?.id ?? "");
  const [submitting, setSubmitting] = useState(false);

  // Default the client select to the first client once they load.
  useEffect(() => {
    if (clientId === "" && clients.length > 0) setClientId(clients[0].id);
  }, [clients, clientId]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/api/users", {
        email,
        password,
        role,
        clientId: role === "client" ? clientId || null : null,
      });
      toast.success(`Created ${email}`);
      setEmail("");
      setPassword("");
      setRole("client");
      await onCreated();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not create user");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="mt-5 rounded-lg border border-ink-200 bg-ink-50/50 p-4">
      <h3 className="text-sm font-semibold text-ink-800">Add a user</h3>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <Field label="Email">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input"
            placeholder="person@company.com"
          />
        </Field>
        <Field label={`Initial password (min ${MIN_PASSWORD_LENGTH})`}>
          <input
            type="text"
            required
            minLength={MIN_PASSWORD_LENGTH}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input"
            placeholder="Share securely"
          />
        </Field>
        <Field label="Role">
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            className="input"
          >
            <option value="client">Client (own portfolio, read-only financials)</option>
            <option value="btzl">BTZL (full access)</option>
          </select>
        </Field>
        {role === "client" && (
          <Field label="Client">
            <select
              value={clientId}
              onChange={(e) => setClientId(e.target.value ? Number(e.target.value) : "")}
              className="input"
              required
            >
              <option value="" disabled>
                Select a client…
              </option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
        )}
      </div>
      <div className="mt-4">
        <button type="submit" disabled={submitting} className="btn-primary">
          {submitting ? "Creating…" : "Create user"}
        </button>
      </div>
    </form>
  );
}

function UserList({
  users,
  currentUserId,
  onChanged,
}: {
  users: AdminUser[];
  currentUserId: number;
  onChanged: () => Promise<void>;
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-ink-200">
      <table className="w-full text-sm">
        <thead className="bg-ink-50 text-left text-xs uppercase tracking-wide text-ink-500">
          <tr>
            <th className="px-4 py-2 font-medium">Email</th>
            <th className="px-4 py-2 font-medium">Role</th>
            <th className="px-4 py-2 font-medium">Client</th>
            <th className="px-4 py-2 text-right font-medium">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-100">
          {users.map((u) => (
            <UserRow
              key={u.id}
              user={u}
              isSelf={u.id === currentUserId}
              onChanged={onChanged}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UserRow({
  user,
  isSelf,
  onChanged,
}: {
  user: AdminUser;
  isSelf: boolean;
  onChanged: () => Promise<void>;
}) {
  const toast = useToast();
  const [resetting, setResetting] = useState(false);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  async function saveReset(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/api/users/${user.id}`, { password: pw });
      toast.success(`Password reset for ${user.email}`);
      setPw("");
      setResetting(false);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not reset password");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!confirm(`Delete ${user.email}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.del(`/api/users/${user.id}`);
      toast.success(`Deleted ${user.email}`);
      await onChanged();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not delete user");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <tr>
        <td className="px-4 py-2.5 font-medium text-ink-900">
          {user.email}
          {isSelf && <span className="ml-2 text-xs text-ink-400">(you)</span>}
        </td>
        <td className="px-4 py-2.5">
          <span
            className={`rounded px-2 py-0.5 text-xs font-medium ${
              user.role === "btzl"
                ? "bg-accent-50 text-accent-700"
                : "bg-ink-100 text-ink-600"
            }`}
          >
            {user.role === "btzl" ? "BTZL" : "Client"}
          </span>
        </td>
        <td className="px-4 py-2.5 text-ink-600">{user.clientName ?? "—"}</td>
        <td className="px-4 py-2.5 text-right">
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setResetting((r) => !r)}
              className="text-sm font-medium text-accent hover:underline"
            >
              {resetting ? "Cancel" : "Reset password"}
            </button>
            {!isSelf && (
              <button
                onClick={remove}
                disabled={busy}
                className="text-sm font-medium text-critical-700 hover:underline"
              >
                Delete
              </button>
            )}
          </div>
        </td>
      </tr>
      {resetting && (
        <tr className="bg-ink-50/60">
          <td colSpan={4} className="px-4 py-3">
            <form onSubmit={saveReset} className="flex flex-wrap items-end gap-3">
              <Field label={`New password for ${user.email} (min ${MIN_PASSWORD_LENGTH})`}>
                <input
                  type="text"
                  required
                  minLength={MIN_PASSWORD_LENGTH}
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  className="input w-72"
                  placeholder="Share securely"
                  autoFocus
                />
              </Field>
              <button type="submit" disabled={busy} className="btn-primary">
                {busy ? "Saving…" : "Set password"}
              </button>
            </form>
            <p className="mt-2 text-xs text-ink-500">
              The user will be signed out of all sessions and must use the new password.
            </p>
          </td>
        </tr>
      )}
    </>
  );
}

/* --------------------- Admin: client page visibility ---------------------- */

function PageAccess() {
  const toast = useToast();
  const [clients, setClients] = useState<Client[]>([]);
  const [clientId, setClientId] = useState<number | "">("");
  const [pages, setPages] = useState<Record<PageKey, boolean> | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get<{ clients: Client[] }>("/api/clients")
      .then((d) => {
        setClients(d.clients);
        if (d.clients[0]) setClientId(d.clients[0].id);
      })
      .catch((err) => toast.error(err instanceof ApiClientError ? err.message : "Could not load clients"))
      .finally(() => setLoading(false));
  }, [toast]);

  useEffect(() => {
    if (clientId === "") return;
    setPages(null);
    api
      .get<ClientPageAccessResponse>(`/api/clients/${clientId}/pages`)
      .then((d) => setPages(d.pages))
      .catch((err) => toast.error(err instanceof ApiClientError ? err.message : "Could not load page access"));
  }, [clientId, toast]);

  async function save() {
    if (clientId === "" || !pages) return;
    setSaving(true);
    try {
      const d = await api.put<ClientPageAccessResponse>(`/api/clients/${clientId}/pages`, { pages });
      setPages(d.pages);
      toast.success("Page access saved");
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not save page access");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="card p-6">
      <h2 className="text-base font-semibold text-ink-900">Client page access</h2>
      <p className="mt-0.5 text-sm text-ink-500">
        Choose which views a client can open. Settings stays available to everyone.
      </p>

      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : (
        <>
          {clients.length > 1 && (
            <Field label="Client">
              <select
                value={clientId}
                onChange={(e) => setClientId(e.target.value ? Number(e.target.value) : "")}
                className="input mt-3 max-w-xs"
              >
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
          )}

          <div className="mt-4 space-y-2">
            {pages === null ? (
              <div className="flex justify-center py-6">
                <Spinner />
              </div>
            ) : (
              PAGE_KEYS.map((key) => (
                <label
                  key={key}
                  className="flex items-center justify-between rounded-lg border border-ink-200 px-4 py-2.5"
                >
                  <span className="text-sm font-medium text-ink-800">{PAGE_LABELS[key]}</span>
                  <input
                    type="checkbox"
                    checked={pages[key]}
                    onChange={(e) => setPages({ ...pages, [key]: e.target.checked })}
                    className="h-4 w-4 accent-accent"
                  />
                </label>
              ))
            )}
          </div>

          <div className="mt-4">
            <button onClick={save} disabled={saving || pages === null} className="btn-primary">
              {saving ? "Saving…" : "Save page access"}
            </button>
          </div>
        </>
      )}
    </section>
  );
}

/* -------------------------------- helpers --------------------------------- */

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-ink-700">{label}</span>
      {children}
    </label>
  );
}
